import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useLocation } from "react-router-dom"

import { privateRoutes } from "router/routes"

import { useAuth, useAsideMenu, useTheme } from "hooks"
import { 
    OCODE_GENDERS_LIST, 
    operators, 
    HORS_AM_GENDER, 
    AM_GENDER, 
    COLLECTIVITE_GENDER, 
    BC_OPERATOR, 
    partnerChainList, 
    VELOCISTE_GENDER, 
    LECLERC_ORDER_TAGS_URL, 
    GIANT_STORES_ORDER_TAGS_URL, 
    EASY_CASH_ORDER_TAGS_URL, 
    CASH_CONVERTER_ORDER_TAGS_URL, 
    BICYCODE_ASSO_ORDER_TAGS_URL, 
    PRO_ORDER_TAGS_DEFAULT_URL,
    YAMAHA_ORDER_TAGS_URL
} from "utils/constants"
import { domainPathname } from "utils/functions"

import { 
    AccountIcon, 
    BikeIcon, 
    TeamIcon, 
    DashboardIcon,
    FAQIcon,
    HistoryIcon,
    UserIcon,
    FileImportIcon,
    TagIcon
} from "components/Elements/Icons"
import { isFrenchLanguage } from "utils/i18n"


export const MenuLinks = () => {
    const [operatorsUser, setOperatorsUser] = React.useState([])

    const { pathname } = useLocation()
    const { toggleAsideMenu } = useAsideMenu()
    const { user, entity } = useAuth()
    const { currentTheme } = useTheme()
    const { t } = useTranslation();

    const linkFAQ = {
        fr:'https://www.notion.so/faq-store/f2f8fd81df3444debb7f8f9024c5fbd4?v=9664306864894128b8f298bfea3a6481',
        en:'https://www.notion.so/faq-store/ed8b036decec47ddaf2b98c58ba61b8c?v=730b8dcb9ff14d29a1bb95d6d9906bfa'

    }


    const entityRole = entity?.entity_data?.entity_permission?.role
    const entityGender = entity?.gender
    const isAllowedToUseBikeCollectionImport = entity?.entity_data?.import_allowed || false
    const isMANAGEBIKES = entityRole === "MANAGEBIKES"
    const isMANAGEENTITIES = entityRole === "MANAGEENTITIES"
    const isAsso = entityGender === HORS_AM_GENDER || entityGender === AM_GENDER || entityGender === COLLECTIVITE_GENDER
    const isUserWithBCAsso = isMANAGEBIKES && isAsso && operatorsUser.some(operator => operator.id_api === BC_OPERATOR)
    const isPro = entityGender === VELOCISTE_GENDER
    const isLeclercEntity = entity?.entity_data?.code_chaine === partnerChainList.find(chain => chain.value === "LECL")?.value
    const isEasyCashEntity = entity?.entity_data?.code_chaine === partnerChainList.find(chain => chain.value === "EACA")?.value
    const isCashConverterEntity = entity?.entity_data?.code_chaine === partnerChainList.find(chain => chain.value === "CACO")?.value
    const isGiantStoresConverterEntity = entity?.entity_data?.code_chaine === partnerChainList.find(chain => chain.value === "GIAN")?.value
    const isYamahaEntity = entity?.entity_data?.code_chaine === partnerChainList.find(chain => chain.value === "YAMA")?.value

    /**
     * Return the order tags URL if available. Return `null` instead.
     * @returns {string | null}
     */
    const getOrderTagsUrl = () => {
        if (isUserWithBCAsso) return BICYCODE_ASSO_ORDER_TAGS_URL
        if (isPro) {
            if (isLeclercEntity) return LECLERC_ORDER_TAGS_URL
            if (isEasyCashEntity) return EASY_CASH_ORDER_TAGS_URL
            if (isCashConverterEntity) return CASH_CONVERTER_ORDER_TAGS_URL
            if (isGiantStoresConverterEntity) return GIANT_STORES_ORDER_TAGS_URL
            if (isYamahaEntity) return YAMAHA_ORDER_TAGS_URL
            return PRO_ORDER_TAGS_DEFAULT_URL
        }
        return null
    }

    // On pathname change, close the asideMenu
    React.useEffect(() => toggleAsideMenu(false), [pathname]) //eslint-disable-line

    const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)
    const isBCTheme = currentTheme?.id === "bicycode"
    const isMFCTheme = currentTheme?.id === "mfc"
    const isAvailableToOrderTags = isBCTheme

    // Filter allowed operators
    React.useEffect(() => {
        if (entity) {
            const allowedOperators = operators.filter(op => entity?.entity_data?.entity_permission?.operateurs.includes(op.id_api))
            setOperatorsUser(allowedOperators)
        }
    }, [entity])

    return (
        <nav className="c-aside__nav">
            {isMANAGEENTITIES &&
                <>
                    <div className={
                            "c-aside__item px-5 " +
                            ((pathname === privateRoutes.validatedPartner.path || pathname.startsWith(privateRoutes.addPartner.path) || pathname.startsWith(privateRoutes.partner.basePath) || pathname.startsWith(privateRoutes.teamPartner.basePath)) ? "is-active" : "")
                        }
                    >
                        <DashboardIcon size={28} />
                        <NavLink to={privateRoutes.validatedPartner.path} className="p-5">{t("menu.manage_partners")}</NavLink>
                    </div>

                    <div className={"c-aside__item px-5 " + (pathname === privateRoutes.bicycodeAdmin.path ? "is-active" : "")}>
                        <TeamIcon size={28} />
                        <NavLink to={privateRoutes.bicycodeAdmin.path} className="p-5">{t("menu.manage_admin")}</NavLink>
                    </div>
                </>
            }

            <div className={"c-aside__item px-5 " + (pathname === privateRoutes.searchBike.path ? "is-active" : "")}>
                <BikeIcon size={28} />
                <NavLink to={privateRoutes.searchBike.path} className="p-5">
                    {t(isMANAGEENTITIES ? "menu.search_bike" : "menu.activate_bike")}
                </NavLink>
            </div>

            {isAdminOCode &&
                <div className={"c-aside__item px-5 " + (pathname === privateRoutes.searchUser.path ? "is-active" : "")}>
                    <UserIcon size={28} />
                    <NavLink to={privateRoutes.searchUser.path} className="p-5">
                        {t("menu.search_user")}
                    </NavLink>
                </div>
            }

            {isMANAGEBIKES &&
                <>
                    { isAllowedToUseBikeCollectionImport && <div className={"c-aside__item px-5 " + (pathname === privateRoutes.bikeCollectionImport.path ? "is-active" : "")}>
                        <FileImportIcon size="28" />
                        <NavLink to={privateRoutes.bikeCollectionImport.path} className="p-5">{t("menu.bike_collection_import")}</NavLink>
                    </div> }
                    <div className={"c-aside__item px-5 " + (pathname === privateRoutes.team.path ? "is-active" : "")}>
                        <TeamIcon size={28} />
                        <NavLink to={privateRoutes.team.path} className="p-5">{t("menu.manage_team")}</NavLink>
                    </div>
                    <div className={"c-aside__item px-5 " + (pathname === privateRoutes.shop.path ? "is-active" : "")}>
                        <AccountIcon size={28} />
                        <NavLink to={privateRoutes.shop.path} className="p-5">{t("menu.store")}</NavLink>
                    </div>
                    <div className={"c-aside__item px-5 " + (pathname === privateRoutes.history.path ? "is-active" : "")}>
                        <HistoryIcon size={30} />
                        <NavLink to={privateRoutes.history.path} className="p-5">{t("menu.history")}</NavLink>
                    </div>
                </>
            }

            {isAvailableToOrderTags &&
                getOrderTagsUrl() &&
                <div className={"c-aside__item px-5 " + (pathname === privateRoutes.history.path ? "is-active" : "")}>
                <TagIcon size={30} />
                <a 
                    href={getOrderTagsUrl()} 
                    rel="noreferrer" 
                    className="p-5" 
                    target="_blank"
                    >
                    {t("menu.order_tags")}
                </a>
                </div>
            }

            {
            //Link Faq to notion page
            currentTheme?.id !== "ocode" &&
                <div className="c-aside__item px-5">
                    <FAQIcon size={28} />
                    <a href={isFrenchLanguage() ? linkFAQ.fr : linkFAQ.en} target="_blank" rel="noopener noreferrer" className="p-5">{t("menu.faq")}</a>
                </div>
            }
        </nav>
    )
}
